/**
 * SortableSectionCard
 * A draggable card representing a page section.
 */

import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import {
  GripVertical,
  Eye,
  EyeOff,
  Pencil,
  Copy,
  Trash2,
  MoreHorizontal,
} from 'lucide-react'
import * as LucideIcons from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { Badge } from '@/components/ui/badge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import {
  useToggleSectionVisibility,
  useDeleteSection,
  useDuplicateSection,
} from '@/hooks/use-page-sections'
import { SECTION_REGISTRY } from '@limo/shared'
import { cn } from '@/lib/utils'
import type { PageSection } from '@limo/database'

interface SortableSectionCardProps {
  section: PageSection
  index: number
  pageId: string
  onEdit: () => void
}

export function SortableSectionCard({
  section,
  index,
  pageId,
  onEdit,
}: SortableSectionCardProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: section.id })

  const toggleVisibility = useToggleSectionVisibility()
  const deleteSection = useDeleteSection()
  const duplicateSection = useDuplicateSection()

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  // Get section definition from registry
  const definition = SECTION_REGISTRY[section.section_type]
  const sectionName = definition?.name || section.section_type
  const sectionDescription = definition?.description || ''

  // Get the icon component from Lucide
  const IconComponent = definition?.icon
    ? (LucideIcons as Record<string, React.ComponentType<{ className?: string }>>)[
        definition.icon
      ] || LucideIcons.FileText
    : LucideIcons.FileText

  const handleToggleVisibility = () => {
    toggleVisibility.mutate({
      sectionId: section.id,
      isVisible: !section.is_visible,
    })
  }

  const handleDuplicate = () => {
    duplicateSection.mutate({ sectionId: section.id })
  }

  const handleDelete = () => {
    deleteSection.mutate({ sectionId: section.id, pageId })
  }

  return (
    <Card
      ref={setNodeRef}
      style={style}
      className={cn(
        'transition-all',
        isDragging && 'opacity-50 shadow-lg',
        !section.is_visible && 'opacity-60 bg-muted/50'
      )}
    >
      <CardContent className="flex items-center gap-4 py-4">
        {/* Drag Handle */}
        <button
          className="cursor-grab active:cursor-grabbing touch-none"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-5 w-5 text-muted-foreground" />
        </button>

        {/* Section Icon */}
        <div
          className={cn(
            'rounded-lg p-2 shrink-0',
            section.is_visible ? 'bg-primary/10' : 'bg-muted'
          )}
        >
          <IconComponent
            className={cn(
              'h-5 w-5',
              section.is_visible ? 'text-primary' : 'text-muted-foreground'
            )}
          />
        </div>

        {/* Section Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-medium truncate">{sectionName}</h3>
            <Badge variant="outline" className="text-xs shrink-0">
              #{index + 1}
            </Badge>
            {!section.is_visible && (
              <Badge variant="secondary" className="text-xs shrink-0">
                <EyeOff className="h-3 w-3 mr-1" />
                Hidden
              </Badge>
            )}
          </div>
          {sectionDescription && (
            <p className="text-sm text-muted-foreground truncate">
              {sectionDescription}
            </p>
          )}
        </div>

        {/* Visibility Toggle */}
        <div className="flex items-center gap-2 shrink-0">
          <Switch
            checked={section.is_visible}
            onCheckedChange={handleToggleVisibility}
            disabled={toggleVisibility.isPending}
            aria-label={section.is_visible ? 'Hide section' : 'Show section'}
          />
        </div>

        {/* Edit Button */}
        <Button variant="outline" size="sm" onClick={onEdit}>
          <Pencil className="h-4 w-4 mr-1" />
          Edit
        </Button>

        {/* More Actions Menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon">
              <MoreHorizontal className="h-4 w-4" />
              <span className="sr-only">More actions</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={onEdit}>
              <Pencil className="mr-2 h-4 w-4" />
              Edit Content
            </DropdownMenuItem>
            <DropdownMenuItem onClick={handleToggleVisibility}>
              {section.is_visible ? (
                <>
                  <EyeOff className="mr-2 h-4 w-4" />
                  Hide Section
                </>
              ) : (
                <>
                  <Eye className="mr-2 h-4 w-4" />
                  Show Section
                </>
              )}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={handleDuplicate}>
              <Copy className="mr-2 h-4 w-4" />
              Duplicate
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <DropdownMenuItem
                  onSelect={(e) => e.preventDefault()}
                  className="text-destructive focus:text-destructive"
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete Section
                </DropdownMenuItem>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete Section</AlertDialogTitle>
                  <AlertDialogDescription>
                    Are you sure you want to delete this {sectionName} section?
                    This action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleDelete}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </DropdownMenuContent>
        </DropdownMenu>
      </CardContent>
    </Card>
  )
}
